import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ContactChurnedBadge } from "@/components/journey/ContactChurnedBadge";
import { CustomerMemoryIntake } from "@/components/journey/CustomerMemoryIntake";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  createCustomerBinding,
  destroyCustomerBinding,
  listCustomerBindings,
  type JourneyCustomerBinding,
} from "@/lib/journey-bindings";
import {
  JourneyApiError,
  listJourneys,
  type JourneyDefinition,
} from "@/lib/journey-api";
import {
  ApiError,
  contactTypeLabel,
  genderLabel,
  proposeWecomExternalContactMemory,
  updateWecomExternalContact,
  type ContactWikiPage,
  type WecomExternalContact,
  formatWecomTagLabel,
} from "@/lib/wecom-external-contacts";
import { kbPagePath, kbWorkbenchPath } from "@/lib/kb-routes";
import { formatFollowUserids } from "@/lib/wecom-users";

/** 下拉「不绑定」哨兵 */
const JOURNEY_NONE = "none";

/** 客户记忆：AI 录入 + 确认落库 */
function CustomerMemoryCard({
  contact,
  onContactChange,
}: {
  contact: WecomExternalContact;
  onContactChange?: (contact: WecomExternalContact) => void;
}) {
  const { accountId } = useAccountWorkspace();

  return (
    <CustomerMemoryIntake
      variant="workbench"
      summary={contact.memory_summary}
      onSummaryChange={(next) => {
        onContactChange?.({ ...contact, memory_summary: next });
      }}
      propose={(facts) => proposeWecomExternalContactMemory(accountId, contact.id, facts)}
      apply={async (summary) => {
        const updated = await updateWecomExternalContact(accountId, contact.id, {
          memory_summary: summary,
        });
        onContactChange?.(updated);
        return updated.memory_summary;
      }}
    />
  );
}

/** 企微侧备注 / 描述 / 标签（来自上次同步或编辑回调拉取） */
function WecomDetailCard({
  contact,
  followUserNameByUserid,
}: {
  contact: WecomExternalContact;
  followUserNameByUserid?: Record<string, string>;
}) {
  const profiles = contact.wecom_detail?.follow_profiles ?? [];

  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-sm font-medium">企微资料</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        备注、描述与企业标签；员工在企微改资料后会自动刷新。
      </p>
      {profiles.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          暂无详情。请先在企微连接页同步客户，或等员工编辑客户资料后再看。
        </p>
      ) : (
        <ul className="mt-2 space-y-3">
          {profiles.map((profile) => {
            const staffLabel =
              followUserNameByUserid?.[profile.userid] || profile.userid;
            const tagLabel = profile.tags.map((t) => formatWecomTagLabel(t)).filter(Boolean).join("、");
            const rows: { label: string; value: string }[] = [
              { label: "备注名", value: profile.remark?.trim() || "—" },
              { label: "描述", value: profile.description?.trim() || "—" },
              {
                label: "备注企业",
                value: profile.remark_corp_name?.trim() || "—",
              },
              {
                label: "备注手机",
                value:
                  profile.remark_mobiles.length > 0
                    ? profile.remark_mobiles.join("、")
                    : "—",
              },
              { label: "企业标签", value: tagLabel || "—" },
            ];
            return (
              <li key={profile.userid} className="space-y-1.5">
                {profiles.length > 1 ? (
                  <p className="text-xs font-medium text-muted-foreground">
                    跟进人 {staffLabel}
                  </p>
                ) : null}
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
                  {rows.map((row) => (
                    <Fragment key={row.label}>
                      <dt className="text-muted-foreground">{row.label}</dt>
                      <dd className="min-w-0 whitespace-pre-wrap break-words">
                        {row.value}
                      </dd>
                    </Fragment>
                  ))}
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** 账户级置顶（跟进人共用同一列表置顶） */
function PinSwitchCard({
  contact,
  onContactChange,
}: {
  contact: WecomExternalContact;
  onContactChange?: (contact: WecomExternalContact) => void;
}) {
  const { accountId } = useAccountWorkspace();
  const [saving, setSaving] = useState(false);
  const pinned = Boolean(contact.pinned);

  async function handleCheckedChange(next: boolean) {
    if (saving || next === pinned) return;
    setSaving(true);
    try {
      const updated = await updateWecomExternalContact(accountId, contact.id, {
        pinned: next,
      });
      onContactChange?.(updated);
      toast.success(next ? "已置顶（账户内共用）" : "已取消置顶");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "无法更新置顶");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`pin-${contact.id}`} className="text-sm font-medium">
          置顶
        </Label>
        <Switch
          id={`pin-${contact.id}`}
          checked={pinned}
          disabled={saving}
          onCheckedChange={handleCheckedChange}
          aria-label="置顶客户"
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {pinned
          ? "已置顶：本账户跟进人都会在列表最上方看到。"
          : "未置顶。开启后固定在客户列表最上方。"}
      </p>
    </div>
  );
}

function ContactAvatar({
  name,
  avatarUrl,
  size = "lg",
}: {
  name: string | null;
  avatarUrl: string | null;
  size?: "md" | "lg";
}) {
  const dim = size === "lg" ? "size-14" : "size-10";
  if (avatarUrl) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label="查看大图"
            className={`${dim} shrink-0 overflow-hidden rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
          >
            <img
              src={avatarUrl}
              alt=""
              className="size-full object-cover"
              referrerPolicy="no-referrer"
            />
          </button>
        </DialogTrigger>
        <DialogContent className="w-auto max-w-[min(96vw,36rem)] gap-0 overflow-hidden p-3 sm:max-w-[min(96vw,36rem)]">
          <DialogTitle className="sr-only">头像大图</DialogTitle>
          <DialogDescription className="sr-only">客户头像预览</DialogDescription>
          <img
            src={avatarUrl}
            alt={name || "客户头像"}
            className="mx-auto max-h-[min(80vh,36rem)] max-w-full rounded-lg object-contain"
            referrerPolicy="no-referrer"
          />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <span
      className={`flex ${dim} shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground`}
    >
      {(name || "?").slice(0, 1)}
    </span>
  );
}

/** 客户 Wiki：只读列出已关联页，或诚实空态 */
function CustomerWikiCard({ pages }: { pages: ContactWikiPage[] }) {
  const { accountId } = useAccountWorkspace();

  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-sm font-medium">客户 Wiki</p>
      {pages.length === 0 ? (
        <div className="mt-1 space-y-1.5">
          <p className="text-xs text-muted-foreground">暂无关联的客户 Wiki 页面</p>
          <Link
            to={kbWorkbenchPath({ accountId: String(accountId) })}
            className="inline-block text-xs text-foreground underline-offset-2 hover:underline"
          >
            打开知识库
          </Link>
        </div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {pages.map((page) => (
            <li key={page.id}>
              <Link
                to={kbPagePath(
                  {
                    accountId: String(accountId),
                    brandId: page.brand_id != null ? String(page.brand_id) : undefined,
                  },
                  page.wiki_id,
                  page.id,
                )}
                className="block truncate text-sm text-foreground underline-offset-2 hover:underline"
                title={page.title}
              >
                {page.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * 旅程绑定：把客户挂到本品牌已启用旅程；绑定后触发规划。
 * 一客一活跃绑定；换绑先解绑再绑。
 */
function JourneyBindingCard({
  contact,
  brandId,
  onBindingChange,
}: {
  contact: WecomExternalContact;
  brandId: string;
  /** 绑定/解绑成功后通知父级刷新聊天计划 */
  onBindingChange?: () => void;
}) {
  const { accountId } = useAccountWorkspace();
  const [journeys, setJourneys] = useState<JourneyDefinition[]>([]);
  const [binding, setBinding] = useState<JourneyCustomerBinding | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const selectId = `journey-binding-${contact.id}`;

  const load = useCallback(async () => {
    if (!accountId || !brandId || !contact.external_userid) return;
    setLoaded(false);
    try {
      const [journeysPayload, bindingsPayload] = await Promise.all([
        listJourneys(accountId, brandId),
        listCustomerBindings(accountId, {
          brandId,
          externalUserid: contact.external_userid,
        }),
      ]);
      setJourneys(
        (journeysPayload.journeys ?? []).filter((j) => j.status === "enabled"),
      );
      setBinding(bindingsPayload.customer_bindings?.[0] ?? null);
      setLoaded(true);
    } catch (caught) {
      toast.error(
        caught instanceof JourneyApiError || caught instanceof ApiError
          ? caught.message
          : "无法加载旅程绑定",
      );
      setLoaded(true);
    }
  }, [accountId, brandId, contact.external_userid]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedValue = binding ? String(binding.journey_definition_id) : JOURNEY_NONE;
  const boundJourneyName =
    journeys.find((j) => j.id === binding?.journey_definition_id)?.name ??
    (binding ? `旅程 #${binding.journey_definition_id}` : null);

  async function handleChange(nextValue: string | null) {
    const next = nextValue ?? JOURNEY_NONE;
    if (next === selectedValue || saving) return;
    setSaving(true);
    try {
      // 换绑或解绑：先结束当前活跃绑定
      if (binding) {
        await destroyCustomerBinding(accountId, binding.id);
        setBinding(null);
      }
      if (next === JOURNEY_NONE) {
        toast.success("已解除旅程绑定");
        onBindingChange?.();
        return;
      }
      const created = await createCustomerBinding(accountId, {
        journey_definition_id: next,
        external_userid: contact.external_userid,
      });
      setBinding(created);
      toast.success("已绑定旅程，正在生成触达计划");
      onBindingChange?.();
    } catch (caught) {
      toast.error(
        caught instanceof JourneyApiError || caught instanceof ApiError
          ? caught.message
          : "无法更新旅程绑定",
      );
      void load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-sm font-medium">旅程绑定</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        绑到一条已启用旅程后，系统会为该客户生成触达计划。
      </p>
      <div className="mt-2 space-y-2">
        <Label htmlFor={selectId} className="sr-only">
          选择旅程
        </Label>
        <Select
          value={selectedValue}
          onValueChange={(v) => void handleChange(v)}
          disabled={!loaded || saving || journeys.length === 0}
        >
          <SelectTrigger id={selectId} aria-label="绑定旅程" className="w-full">
            <SelectValue placeholder="选择旅程" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={JOURNEY_NONE}>不绑定</SelectItem>
            {journeys.map((journey) => (
              <SelectItem key={journey.id} value={String(journey.id)}>
                {journey.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!loaded ? (
          <p className="text-xs text-muted-foreground">加载中…</p>
        ) : journeys.length === 0 ? (
          <p className="text-xs text-muted-foreground">本品牌暂无已启用旅程，请先在旅程设计中启用。</p>
        ) : binding && boundJourneyName ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">当前：{boundJourneyName}</p>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={saving}
              onClick={() => void handleChange(JOURNEY_NONE)}
            >
              解绑
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">未绑定。选择旅程即可开始规划。</p>
        )}
      </div>
    </div>
  );
}

/** 客户资料面板：置顶 + 旅程绑定 + 客户记忆 + 企微资料 + 客户 Wiki */
export function CustomerProfilePanel({
  contact,
  isLoading,
  error,
  brandId,
  followUserNameByUserid,
  onContactChange,
  onBindingChange,
}: {
  contact: WecomExternalContact | null;
  isLoading: boolean;
  error: string | null;
  /** 当前旅程品牌（绑定作用域） */
  brandId?: string | null;
  /** userid → 展示名；未提供时回退为 userid */
  followUserNameByUserid?: Record<string, string>;
  /** 偏好保存成功后回写父级联系人状态 */
  onContactChange?: (contact: WecomExternalContact) => void;
  /** 绑定变更后刷新聊天侧计划 */
  onBindingChange?: () => void;
}) {
  if (isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">正在加载资料…</p>;
  }

  if (error) {
    return (
      <p className="p-4 text-sm text-destructive" role="alert">
        {error}
      </p>
    );
  }

  if (!contact) {
    return <p className="p-4 text-sm text-muted-foreground">未选择客户</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex items-start gap-3">
        <ContactAvatar name={contact.name} avatarUrl={contact.avatar_url} />
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-base font-medium">{contact.name || "未获取昵称"}</p>
            <ContactChurnedBadge churnedAt={contact.churned_at} />
          </div>
          <p
            className="truncate font-mono text-xs text-muted-foreground"
            title={contact.external_userid}
          >
            {contact.external_userid}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-muted-foreground">性别</dt>
        <dd>{genderLabel(contact.gender)}</dd>
        <dt className="text-muted-foreground">类型</dt>
        <dd>{contactTypeLabel(contact.contact_type)}</dd>
        <dt className="text-muted-foreground">跟进人</dt>
        <dd className="min-w-0 break-all">
          {contact.follow_userids.length > 0
            ? formatFollowUserids(
                contact.follow_userids,
                followUserNameByUserid ?? {},
                "、",
              )
            : "—"}
        </dd>
      </dl>

      <div className="space-y-2">
        <PinSwitchCard contact={contact} onContactChange={onContactChange} />
        {brandId ? (
          <JourneyBindingCard
            contact={contact}
            brandId={brandId}
            onBindingChange={onBindingChange}
          />
        ) : null}
        <CustomerMemoryCard contact={contact} onContactChange={onContactChange} />
        <WecomDetailCard
          contact={contact}
          followUserNameByUserid={followUserNameByUserid}
        />
        <CustomerWikiCard pages={contact.wiki_pages ?? []} />
      </div>
    </div>
  );
}
